import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, App, Button, Descriptions, Popconfirm, Progress, Segmented, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloudUploadOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  cancelBulkBatch,
  confirmBulkBatch,
  downloadBulkReport,
  getBulkBatch,
  listBulkRows,
  retryFailedBulkBatch,
  type BulkBatch,
  type BulkRow,
  type BulkRowStatus,
} from '@/api/bulk';
import { normalizeApiError } from '@/api/errors';
import { casePath } from '@/api/runPath';
import {
  BATCH_STATUS_COLOR,
  BATCH_STATUS_LABEL,
  canCancel,
  canConfirm,
  canRetry,
  confirmSummary,
  isLive,
  progressPercent,
  ROW_STATUS_COLOR,
  ROW_STATUS_LABEL,
  SUBJECT_TYPE_LABEL,
} from '@/bulk/bulkModel';
import { ErrorState } from '@/components/ErrorState';
import { PageHeader } from '@/components/PageHeader';
import { TableSkeleton } from '@/components/TableSkeleton';
import { usePageTitle } from '@/components/pageTitle';
import { ERASED_LABEL, erasedOr } from '@/lib/erased';

const { Text } = Typography;

const PAGE_SIZE = 50;
const POLL_MS = 2000;

type RowFilter = 'ALL' | 'INVALID' | 'FAILED' | 'STARTED' | 'DUPLICATE';

/** The bulk read answers `[erased]` for an erased row, not the shell, and the downloaded report keeps it. */
const bulkValue = (v?: string | null) => (v === '[erased]' ? ERASED_LABEL : (erasedOr(v) ?? '—'));

const ts = (v?: string | null) => (v ? new Date(v).toLocaleString() : '—');

export function BulkBatchPage() {
  const { batchId = '' } = useParams();
  usePageTitle('Bulk batch');
  const { message } = App.useApp();
  const [batch, setBatch] = useState<BulkBatch | null>(null);
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [rowTotal, setRowTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<RowFilter>('ALL');
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    try {
      const status: BulkRowStatus | undefined = filter === 'ALL' ? undefined : filter;
      const [b, r] = await Promise.all([getBulkBatch(batchId), listBulkRows(batchId, page, PAGE_SIZE, status)]);
      setBatch(b);
      setRows(r.items);
      setRowTotal(r.totalCount);
      setError(null);
    } catch (e) {
      setError(normalizeApiError(e).message);
    }
  }, [batchId, page, filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const live = batch ? isLive(batch.status) : false;
  useEffect(() => {
    if (!live) return;
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [live, load]);

  const act = useCallback(
    async (work: () => Promise<BulkBatch>) => {
      setActing(true);
      try {
        setBatch(await work());
        await load();
      } catch (e) {
        const err = normalizeApiError(e);
        void message.error(`${err.code}: ${err.message}`);
      } finally {
        setActing(false);
      }
    },
    [load, message],
  );

  const download = useCallback(async () => {
    try {
      await downloadBulkReport(batchId);
    } catch (e) {
      void message.error(normalizeApiError(e).message);
    }
  }, [batchId, message]);

  if (error && !batch) {
    return (
      <div className="ol-page">
        <ErrorState what="Could not load this batch" detail={error} onRetry={() => void load()} />
      </div>
    );
  }
  if (!batch) {
    return (
      <div className="ol-page">
        <TableSkeleton rows={8} />
      </div>
    );
  }

  const c = batch.counts;
  const columns: ColumnsType<BulkRow> = [
    { title: 'Row', dataIndex: 'rowNo', width: 64, align: 'right' },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (s: BulkRowStatus) => (
        <Tag bordered={false} color={ROW_STATUS_COLOR[s]}>
          {ROW_STATUS_LABEL[s]}
        </Tag>
      ),
    },
    { title: 'Reference', dataIndex: 'reference', ellipsis: true, render: (v) => bulkValue(v) },
    { title: 'Subject', dataIndex: 'subjectLabel', ellipsis: true, render: (v) => bulkValue(v) },
    {
      title: 'What happened',
      render: (_, r) => {
        if (r.errors.length > 0) {
          return (
            <Space direction="vertical" size={2} data-testid="bulk-row-errors">
              {r.errors.map((e) => (
                <Text key={`${e.column}-${e.code}`} type="danger" style={{ fontSize: 12.5 }}>
                  {e.column && <Text className="ol-mono" type="danger" style={{ fontSize: 12 }}>{e.column}: </Text>}
                  {e.message}
                </Text>
              ))}
              {r.priorBatchId && (
                <Link to={`/bulk/${encodeURIComponent(r.priorBatchId)}`} style={{ fontSize: 12.5 }}>
                  Open that batch
                </Link>
              )}
            </Space>
          );
        }
        if (r.status === 'GROUPED') return <Text type="secondary">With row {r.leaderRow}</Text>;
        return null;
      },
    },
    {
      title: 'Run',
      width: 210,
      render: (_, r) =>
        r.runId ? (
          <Link
            to={casePath(r.runId, r.entityId)}
            data-testid="bulk-run-link"
          >
            <Tag bordered={false} color={r.runState === 'FAILED' ? 'error' : r.runState === 'COMPLETED' ? 'success' : 'processing'}>
              {r.runOutcome ?? r.runState}
            </Tag>
            Open case
          </Link>
        ) : (
          '—'
        ),
    },
    ...(batch.screenConfigId
      ? ([
          {
            title: 'Room link',
            width: 130,
            render: (_: unknown, r: BulkRow) =>
              r.inviteStatus ? (
                <Tag bordered={false} color={r.inviteStatus === 'SENT' ? 'success' : 'error'} title={r.inviteProvider ?? undefined}>
                  {r.inviteStatus === 'SENT' ? 'Sent' : 'Not sent'}
                </Tag>
              ) : (
                '—'
              ),
          },
        ] as ColumnsType<BulkRow>)
      : []),
  ];

  return (
    <div className="ol-page">
      <PageHeader
        title={batch.name}
        icon={<CloudUploadOutlined />}
        subtitle={
          <Link to="/bulk">
            All batches
          </Link>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              Refresh
            </Button>
            <Button icon={<DownloadOutlined />} onClick={() => void download()} data-testid="bulk-report">
              Download the report
            </Button>
            {canRetry(batch) && (
              <Button loading={acting} onClick={() => void act(() => retryFailedBulkBatch(batch.batchId))} data-testid="bulk-retry">
                Retry failed rows
              </Button>
            )}
            {canCancel(batch) && (
              <Popconfirm
                title={batch.status === 'RUNNING' ? 'Stop the rows not yet started? Runs already started carry on.' : 'Cancel this batch?'}
                onConfirm={() => void act(() => cancelBulkBatch(batch.batchId))}
              >
                <Button danger loading={acting} data-testid="bulk-cancel">
                  Cancel
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      />

      {canConfirm(batch) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="bulk-confirm-box"
          message="Nothing has started yet."
          description={
            <Space direction="vertical">
              <Text>{confirmSummary(batch)}</Text>
              <Text type="secondary">If you do not confirm by {ts(batch.expiresAt)}, this batch expires and its data is cleared.</Text>
              <Button type="primary" loading={acting} onClick={() => void act(() => confirmBulkBatch(batch.batchId))} data-testid="bulk-confirm">
                Start {c.valid} run{c.valid === 1 ? '' : 's'}
              </Button>
            </Space>
          }
        />
      )}
      {batch.status === 'NOTHING_TO_START' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No row in this file can start."
          description="Fix the rows below and upload the file again."
        />
      )}

      <Descriptions size="small" column={3} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Status">
          <Tag bordered={false} color={BATCH_STATUS_COLOR[batch.status]} data-testid="bulk-status">
            {BATCH_STATUS_LABEL[batch.status]}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Flow">
          <Text className="ol-mono">
            {batch.flowId} v{batch.flowVersion}
          </Text>
        </Descriptions.Item>
        <Descriptions.Item label="Type">{SUBJECT_TYPE_LABEL[batch.subjectType]}</Descriptions.Item>
        <Descriptions.Item label="Uploaded by">
          {batch.createdBy}, {ts(batch.createdAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Confirmed by">{batch.confirmedBy ? `${batch.confirmedBy}, ${ts(batch.confirmedAt)}` : '—'}</Descriptions.Item>
        <Descriptions.Item label="Finished">{ts(batch.finishedAt)}</Descriptions.Item>
        {batch.repeatReason && (
          <Descriptions.Item label="Run again because" span={3}>
            {batch.repeatReason}
            {batch.repeatOfBatchId && (
              <>
                {' '}
                (<Link to={`/bulk/${encodeURIComponent(batch.repeatOfBatchId)}`}>earlier batch</Link>)
              </>
            )}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Progress percent={progressPercent(batch)} style={{ marginBottom: 12 }} data-testid="bulk-progress" />
      <Space size={32} wrap style={{ marginBottom: 20 }}>
        <Statistic title="Rows" value={batch.totalRows} />
        <Statistic title="Ready" value={c.valid} />
        <Statistic title="With errors" value={c.invalid} />
        <Statistic title="Already started" value={c.duplicate} />
        {c.grouped > 0 && <Statistic title="In a group" value={c.grouped} />}
        <Statistic title="Queued" value={c.queued} />
        <Statistic title="Runs started" value={c.started} />
        <Statistic title="Failed" value={c.failed} />
        <Statistic title="Runs active" value={c.runsActive} />
        <Statistic title="Runs finished" value={c.runsCompleted + c.runsFailed} />
      </Space>

      <div style={{ marginBottom: 12 }}>
        <Segmented<RowFilter>
          value={filter}
          onChange={(v) => {
            setFilter(v);
            setPage(1);
          }}
          options={[
            { label: 'All', value: 'ALL' },
            { label: 'With errors', value: 'INVALID' },
            { label: 'Failed', value: 'FAILED' },
            { label: 'Started', value: 'STARTED' },
            { label: 'Already started', value: 'DUPLICATE' },
          ]}
          data-testid="bulk-row-filter"
        />
      </div>
      <Table
        rowKey="rowNo"
        size="small"
        dataSource={rows}
        columns={columns}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: rowTotal, showSizeChanger: false, onChange: setPage }}
        data-testid="bulk-rows"
      />
    </div>
  );
}
